// MegaData.ts - 超巨型游戏数据文件
// 包含大量NPC、物品、地点、任务、对话、故事等数据

// ========== NPC数据库 (500个NPC) ==========
export interface NPCData {
  id: string;
  name: string;
  description: string;
  personality: string;
  location: string;
  likes: string[];
  dislikes: string[];
  giftPreferences: { item: string; reaction: 'love' | 'like' | 'neutral' | 'dislike' | 'hate' }[];
  schedule: { time: string; location: string; activity: string }[];
  dialoguePool: string[];
  quests: string[];
  relationshipLevel: number;
  maxRelationship: number;
  portrait: string;
  sprite: string;
}

export const NPC_DATABASE: NPCData[] = [
  {
    id: 'npc_001',
    name: '老张',
    description: '村里的老农夫，种了一辈子地',
    personality: '朴实、勤劳、热心',
    location: '村口农田',
    likes: ['蔬菜', '茶叶', '锄头'],
    dislikes: ['懒惰', '浪费'],
    giftPreferences: [
      { item: '新鲜蔬菜', reaction: 'love' },
      { item: '茶叶', reaction: 'like' },
      { item: '锄头', reaction: 'like' },
      { item: '杂草', reaction: 'dislike' }
    ],
    schedule: [
      { time: '06:00', location: '农田', activity: '浇水' },
      { time: '12:00', location: '家', activity: '吃午饭' },
      { time: '15:00', location: '农田', activity: '除草' },
      { time: '18:00', location: '村口', activity: '乘凉聊天' }
    ],
    dialoguePool: [
      '种地可是个技术活，需要耐心和经验。',
      '今年的收成不错，多亏天气好。',
      '要不要我教你一些种地的技巧？',
      '年轻人要脚踏实地，不要好高骛远。',
      '这土地养育了我们几代人，要好好珍惜。'
    ],
    quests: ['quest_001', 'quest_002'],
    relationshipLevel: 0,
    maxRelationship: 10,
    portrait: 'npc_laozhang',
    sprite: 'spr_laozhang'
  },
  {
    id: 'npc_002',
    name: '王婶',
    description: '村里最有名的厨娘，做的饭菜特别香',
    personality: '热情、爱唠叨、厨艺高超',
    location: '村口小饭馆',
    likes: ['食材', '调料', '厨具'],
    dislikes: ['浪费食物', '挑食'],
    giftPreferences: [
      { item: '新鲜食材', reaction: 'love' },
      { item: '调料', reaction: 'like' },
      { item: '厨具', reaction: 'like' },
      { item: '腐烂食物', reaction: 'hate' }
    ],
    schedule: [
      { time: '05:00', location: '厨房', activity: '准备早餐' },
      { time: '08:00', location: '饭馆', activity: '营业' },
      { time: '14:00', location: '厨房', activity: '准备晚餐' },
      { time: '20:00', location: '家', activity: '休息' }
    ],
    dialoguePool: [
      '来尝尝我刚做的红烧肉，可香了！',
      '做菜讲究火候和心情，两者缺一不可。',
      '你要是想学做菜，我可以教你几招。',
      '食材新鲜最重要，其次是用心。',
      '吃饭是人生大事，可不能马虎。'
    ],
    quests: ['quest_003', 'quest_004'],
    relationshipLevel: 0,
    maxRelationship: 10,
    portrait: 'npc_wangshen',
    sprite: 'spr_wangshen'
  },
];

// 生成更多NPC数据
for (let i = 3; i <= 500; i++) {
  const npcTypes = ['农夫', '商人', '猎人', '渔夫', '铁匠', '木匠', '医生', '教师', '道士', '和尚'];
  const personalities = ['热情', '冷漠', '幽默', '严肃', '古怪', '温柔', '暴躁', '懒惰', '勤奋', '神秘'];
  const locations = ['村口', '农田', '森林', '河边', '山顶', '集市', '寺庙', '书院', '医馆', '铁匠铺'];
  
  const npcType = npcTypes[i % npcTypes.length];
  const personality = personalities[i % personalities.length];
  const location = locations[i % locations.length];
  
  NPC_DATABASE.push({
    id: `npc_${String(i).padStart(3, '0')}`,
    name: `${npcType}${i}号`,
    description: `${location}的${npcType}，性格${personality}`,
    personality: personality,
    location: location,
    likes: ['礼物', '帮助', '聊天'],
    dislikes: ['无礼', '欺骗', '偷窃'],
    giftPreferences: [
      { item: '礼物', reaction: 'like' },
      { item: '食物', reaction: i % 3 === 0 ? 'love' : 'like' },
      { item: '工具', reaction: i % 5 === 0 ? 'love' : 'like' }
    ],
    schedule: [
      { time: '08:00', location: location, activity: '工作' },
      { time: '12:00', location: '家', activity: '午饭' },
      { time: '18:00', location: location, activity: '收工' }
    ],
    dialoguePool: [
      `我是${location}的${npcType}，有什么需要帮忙的吗？`,
      `今天天气不错，适合${i % 2 === 0 ? '干活' : '休息'}。`,
      `年轻人要多多努力，不要虚度光阴。`,
      `我这人比较${personality}，有什么得罪的地方请见谅。`,
      `听说最近${location}有些新鲜事，你有兴趣吗？`
    ],
    quests: [`quest_${i * 2}`, `quest_${i * 2 + 1}`],
    relationshipLevel: 0,
    maxRelationship: 10,
    portrait: `npc_${i}`,
    sprite: `spr_${i}`
  });
}

// ========== 物品数据库 (1000个物品) ==========
export interface ItemData {
  id: string;
  name: string;
  description: string;
  category: 'food' | 'tool' | 'material' | 'treasure' | 'quest' | 'decoration' | 'seed' | 'fish' | 'crop' | 'cooked';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic';
  value: number;
  stackable: boolean;
  maxStack: number;
  effects?: { type: string; value: number }[];
  usable: boolean;
  usableEffect?: string;
  craftable: boolean;
  recipe?: { item: string; count: number }[];
  giftable: boolean;
  npcReactions?: { npcId: string; reaction: string }[];
  icon: string;
}

export const ITEM_DATABASE: ItemData[] = [
  {
    id: 'item_001',
    name: '普通鱼竿',
    description: '一根普通的钓鱼竿，适合新手使用',
    category: 'tool',
    rarity: 'common',
    value: 100,
    stackable: false,
    maxStack: 1,
    usable: false,
    craftable: false,
    giftable: true,
    icon: 'rod_common'
  },
  {
    id: 'item_002',
    name: '精致鱼竿',
    description: '做工精良的钓鱼竿，钓鱼成功率更高',
    category: 'tool',
    rarity: 'uncommon',
    value: 500,
    stackable: false,
    maxStack: 1,
    usable: false,
    craftable: true,
    recipe: [{ item: '普通鱼竿', count: 1 }, { item: '竹子', count: 5 }, { item: '丝线', count: 3 }],
    giftable: true,
    icon: 'rod_fine'
  },
  {
    id: 'item_003',
    name: '传说鱼竿',
    description: '传说中的钓鱼神器，可以钓到稀有鱼类',
    category: 'tool',
    rarity: 'legendary',
    value: 5000,
    stackable: false,
    maxStack: 1,
    usable: false,
    craftable: true,
    recipe: [{ item: '精致鱼竿', count: 1 }, { item: '龙鳞', count: 1 }, { item: '凤凰羽', count: 1 }],
    giftable: true,
    icon: 'rod_legend'
  },
];

// 生成更多物品
const itemNames = [
  '锄头', '镰刀', '斧头', '锤子', '锯子', '铲子', '水壶', '篮子', '背包', '钓鱼竿',
  '苹果', '梨', '桃子', '西瓜', '葡萄', '草莓', '香蕉', '橘子', '樱桃', '蓝莓',
  '小麦', '水稻', '玉米', '大豆', '土豆', '红薯', '花生', '芝麻', '棉花', '茶叶',
  '萝卜', '白菜', '青菜', '菠菜', '芹菜', '韭菜', '葱', '姜', '蒜', '辣椒',
  '番茄', '黄瓜', '茄子', '南瓜', '冬瓜', '丝瓜', '苦瓜', '豆角', '豌豆', '豆芽',
  '猪肉', '牛肉', '羊肉', '鸡肉', '鸭肉', '鱼肉', '虾', '蟹', '贝类', '鸡蛋',
  '面包', '馒头', '包子', '饺子', '面条', '米饭', '粥', '汤圆', '粽子', '月饼',
  '铁块', '铜块', '银块', '金块', '玉石', '宝石', '水晶', '珍珠', '玛瑙', '翡翠',
  '木材', '石材', '泥土', '沙子', '煤炭', '矿石', '宝石原石', '稀有矿石', '魔法石', '能量石',
  '草药', '蘑菇', '花朵', '树叶', '树枝', '树皮', '树根', '树脂', '花粉', '花蜜'
];

const rarities: Array<'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic'> = 
  ['common', 'common', 'common', 'uncommon', 'uncommon', 'rare', 'rare', 'epic', 'legendary', 'mythic'];

for (let i = 4; i <= 1000; i++) {
  const baseName = itemNames[i % itemNames.length];
  const rarity = rarities[i % rarities.length];
  const categories: ItemData['category'][] = ['food', 'tool', 'material', 'treasure', 'quest', 'decoration', 'seed', 'fish', 'crop', 'cooked'];
  const category = categories[i % categories.length];
  
  const rarityMultiplier = {
    common: 1,
    uncommon: 2.5,
    rare: 6,
    epic: 15,
    legendary: 40,
    mythic: 100
  };
  
  ITEM_DATABASE.push({
    id: `item_${String(i).padStart(3, '0')}`,
    name: `${rarity === 'common' ? '' : ['粗糙的', '普通的', '精致的', '稀有的', '传说的', '神话的'][['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'].indexOf(rarity)]}${baseName}`,
    description: `${rarity}品质的${baseName}，${category}类物品`,
    category: category,
    rarity: rarity,
    value: Math.floor(10 * rarityMultiplier[rarity] * (1 + i % 10)),
    stackable: category !== 'tool',
    maxStack: category === 'tool' ? 1 : (rarity === 'common' ? 99 : rarity === 'uncommon' ? 50 : rarity === 'rare' ? 20 : 10),
    usable: category === 'food' || category === 'cooked',
    usableEffect: category === 'food' || category === 'cooked' ? '恢复体力' : undefined,
    craftable: i % 3 === 0,
    giftable: true,
    icon: `${category}_${i}`
  });
}

// 导出默认对象
export default {
  NPC_DATABASE,
  ITEM_DATABASE,
};
